import { Configuration, InteractionRequiredAuthError, LogLevel, PublicClientApplication } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID;
const authority = import.meta.env.VITE_AZURE_AD_AUTHORITY;
const redirectUri = import.meta.env.VITE_AZURE_AD_REDIRECT_URI ?? window.location.origin;

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    // Required for Safari: ITP can clear sessionStorage during a cross-origin
    // redirect inside the auth popup. Storing auth state in a cookie instead
    // prevents nonce/state loss and "hash_empty_error" on return.
    storeAuthStateInCookie: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
    },
  },
};

const apiScopes = import.meta.env.VITE_AZURE_AD_SCOPES?.split(',') ?? [];

// Include API scopes so consent for the backend resource is obtained during the initial
// login flow. Without this, acquireTokenSilent fails post-login (no prior consent),
// the popup fallback fires unreliably, and user.roles ends up empty.
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', ...apiScopes],
};

export const apiRequest = {
  scopes: apiScopes,
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Safari preserves sessionStorage across restarts via tab restoration. If a
// previous auth popup didn't complete, MSAL leaves interaction.status =
// "interaction_in_progress" in sessionStorage, causing initialize() to throw
// and leaving inProgress stuck non-None (Sign In button permanently disabled).
// Clear any stale interaction state before creating the instance.
function clearStaleInteractionState(): void {
  try {
    const staleKey = Object.keys(sessionStorage).find((k) =>
      k.includes('interaction.status'),
    );
    if (staleKey) sessionStorage.removeItem(staleKey);
  } catch {
    // sessionStorage may be unavailable in restrictive contexts
  }
}

export async function initializeMsal(): Promise<void> {
  clearStaleInteractionState();
  await msalInstance.initialize();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0] ?? null);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === 'msal:loginSuccess' && event.payload) {
      const payload = event.payload as { account?: { username: string } };
      if (payload.account) {
        msalInstance.setActiveAccount(
          msalInstance.getAccountByUsername(payload.account.username)
        );
      }
    }
  });
}

// Silent-only token acquisition. This is called from the Axios interceptor on every
// request, so interactive fallbacks must NOT be attempted here — concurrent popup calls
// cause an interaction_in_progress deadlock. Interactive re-auth is handled by useAuth.
//
// The silent flow uses a hidden iframe to talk to the AAD authority. When the browser
// blocks third-party cookies (incognito, Brave, ITP) the iframe never reaches AAD and
// `acquireTokenSilent` can hang indefinitely instead of rejecting. We race it against
// a hard timeout so the axios request interceptor never stalls — better to fire the
// request unauthenticated, take a 401, and let the AuthGuard recovery page kick in.
const SILENT_TOKEN_TIMEOUT_MS = 5_000;

export async function acquireAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    return null;
  }

  try {
    const tokenPromise = msalInstance
      .acquireTokenSilent({ ...apiRequest, account })
      .then((response) => response.accessToken);
    const timeoutPromise = new Promise<null>((resolve) => {
      window.setTimeout(() => resolve(null), SILENT_TOKEN_TIMEOUT_MS);
    });
    return await Promise.race([tokenPromise, timeoutPromise]);
  } catch {
    return null;
  }
}

/**
 * Result of an interactive token-recovery attempt.
 * - `recovered`: silent forceRefresh succeeded; the access token is now fresh.
 *   Caller should re-run the failed query (e.g. invalidate meQuery).
 * - `redirecting`: silent failed, redirect to AAD has been initiated. The page
 *   is about to unload; no further work is required by the caller.
 * - `failed`: neither path worked (no active account, redirect threw, etc.).
 *   Caller should surface manual Retry / Sign out controls.
 */
export type TokenRecoveryOutcome = 'recovered' | 'redirecting' | 'failed';

/**
 * Tiered token recovery for use from the AuthGuard verification-failed page.
 *
 * The cheap path (`acquireTokenSilent` with `forceRefresh: true`) re-validates
 * with AAD via the hidden iframe but does NOT unload the page — when the AAD
 * session cookie is alive and only our cached access token went stale, this
 * resolves the issue invisibly.
 *
 * The expensive fallback (`acquireTokenRedirect`) navigates the whole tab to
 * the AAD authority. If AAD still has a valid session it bounces back almost
 * instantly with a fresh token; otherwise the user lands on the AAD sign-in
 * page. `redirectStartPage` preserves the URL the user was on so they return
 * to the same route, not to `/`.
 *
 * The 5s timeout on the silent attempt matches `acquireAccessToken`'s timeout
 * — the iframe can hang indefinitely when third-party cookies are blocked, and
 * we'd rather fall through to redirect than freeze the recovery page.
 */
export async function tryInteractiveTokenRecovery(): Promise<TokenRecoveryOutcome> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // No MSAL account at all — there's nothing to refresh. AuthGuard will
    // render LoginPage once isAuthenticated flips to false; that's a better
    // UX than a redirect-loop on the recovery page.
    return 'failed';
  }

  // Tier 1: silent force-refresh. Bypasses MSAL's token cache to ensure we
  // talk to AAD, not just return the (already expired) cached access token.
  try {
    const silentPromise = msalInstance
      .acquireTokenSilent({ ...apiRequest, account, forceRefresh: true })
      .then((response) => response.accessToken);
    const timeoutPromise = new Promise<null>((resolve) => {
      window.setTimeout(() => resolve(null), SILENT_TOKEN_TIMEOUT_MS);
    });
    const result = await Promise.race([silentPromise, timeoutPromise]);
    if (result) {
      return 'recovered';
    }
    // Timeout — fall through to redirect.
  } catch (error) {
    // InteractionRequiredAuthError is the expected path (AAD session expired).
    // Any other error (network, config) we also treat as "silent didn't work"
    // and let the redirect tier try.
    if (!(error instanceof InteractionRequiredAuthError)) {
      console.warn('Silent token recovery failed with unexpected error:', error);
    }
  }

  // Tier 2: redirect-based interactive recovery. This unloads the page; the
  // caller should treat 'redirecting' as terminal.
  try {
    await msalInstance.acquireTokenRedirect({
      ...apiRequest,
      account,
      redirectStartPage: window.location.href,
    });
    return 'redirecting';
  } catch (error) {
    console.error('Interactive token recovery failed:', error);
    return 'failed';
  }
}
